/**
 * Die Breite, auf die der **Canvas-Inhalt** während einer Größenbewegung
 * festgesetzt wird.
 *
 * Der Inhalt hängt die ganzen 250ms an der RECHTEN Kante und trägt diese eine
 * Zahl (`--canvas-content-width`, `&--motion &__viewport` in
 * `ProjectCanvasPanel.scss`). React Flow sieht damit keine Größenänderung und
 * rechnet während der Bewegung kein einziges Mal nach; die laufende linke
 * Kante gibt die Zettel frei oder deckt sie zu. Ohne das wanderten sie mit —
 * gemessen 659px quer durchs Bild.
 *
 * Die Zahl ist also eine VORHERSAGE: Das Panel ist erst in 250ms dort, wo sie
 * es hinrechnet. Stimmt sie nicht, steht der Inhalt die ganze Bewegung über
 * daneben und rastet im letzten Bild ein — ein Ruck ohne Fehlermeldung.
 *
 * Bewusst ohne DOM-Bezug, wie die übrigen `utils/`: Gemessen wird im Panel,
 * gerechnet hier, geprüft ohne gerendertes Panel.
 */

import type { CanvasPanelState } from '../ProjectCanvas.types'
import { CANVAS_CONTAINER_GAP, clampSplitRatio } from './canvasSplitDrag'

export interface CanvasContentWidthInput {
  /**
   * Die Zeile, in der Aufgabenliste und Panel nebeneinander liegen — das
   * Elternelement des Panels —, und zwar so breit, wie sie am ENDE der
   * Bewegung ist.
   *
   * Meist ist das die Zeile von jetzt. Weicht die **Navigation** mit dem
   * Öffnen (schmales Fenster, #296), wächst sie noch während der Bewegung;
   * diesen Zuwachs rechnet der Aufrufer hinzu (`forecastRowGrowth`, #297).
   * Einen festen Zuschlag gab es schon einmal — damals klappte der Canvas
   * die Navigation IMMER ein; seit `a4934e01` nicht mehr, und der Zuschlag
   * setzte den Inhalt 272px zu weit (`.claude/tasks/canvas-zittern-beim-maximieren.md`).
   * Deshalb kommt er heute von dem, der das Weichen entscheidet.
   */
  rowWidth: number
  /** Anteil der Aufgabenliste an der Zeile, 0..1. Nur `split` braucht ihn. */
  ratio: number
  /** Der Zustand, dessen Breite gefragt ist. */
  state: Exclude<CanvasPanelState, 'closed'>
}

/**
 * `closed` fehlt mit Absicht: Dort gibt es nichts vorherzusagen — das Panel
 * läuft auf null, und festgehalten wird die Breite, die es beim Start der
 * Bewegung noch HAT. Die steht nur im DOM, nicht in dieser Rechnung.
 */
export function predictCanvasContentWidth({
  rowWidth,
  ratio,
  state,
}: CanvasContentWidthInput): number {
  // Geklemmt wie im Stylesheet (`--split`: min 320px, max `100% - 429px`): Ein gespeichertes Verhältnis von vor
  // der neuen Mindestbreite der Liste (#296) setzt das CSS durch, nicht die Zahl.
  return state === 'maximized'
    ? rowWidth - CANVAS_CONTAINER_GAP
    : (1 - clampSplitRatio(ratio, rowWidth)) * rowWidth
}

/**
 * Der Rand, den die Canvas-FLÄCHE trägt (`&--split &__canvas`,
 * `&--maximized &__canvas` in `ProjectCanvasPanel.scss`).
 *
 * Er steht hier, weil die Vorhersage die Breite des PANELS liefert, die
 * eingefrorene Fläche aber innerhalb der Ränder liegt: Sie misst zwei Ränder
 * weniger. Wer ihn vergisst, friert 2px zu breit ein — beim Auftauen sprang
 * der Inhalt um genau diese 2px zurück, sichtbar als Zucken im letzten Bild.
 * Ein Invarianten-Test hält beide Zahlen zusammen.
 */
export const CANVAS_SURFACE_BORDER = 1

/** Die Breite der Fläche innerhalb der Ränder. */
export function canvasSurfaceWidth(panelWidth: number): number {
  return panelWidth - 2 * CANVAS_SURFACE_BORDER
}

export interface CanvasMotionPlan {
  /** Die Breite, auf die die Fläche für die Dauer der Bewegung festgesetzt wird. */
  width: number
  /** Ihr Versatz im ersten Bild. */
  shiftFrom: number
  /** Ihr Versatz am Ende. */
  shiftTo: number
}

/**
 * **Wo die eingefrorene Fläche steht und wohin sie gleitet.**
 *
 * Die Fläche hängt an der RECHTEN Kante und trägt eine feste Breite; React
 * Flow sieht damit keine Größenänderung und rechnet während der ganzen
 * Bewegung kein einziges Mal nach. Zwei Bedingungen muss die Wahl erfüllen:
 *
 * 1. **Sie darf nie schmaler sein als das Panel** — sonst bliebe ein Streifen
 *    des Canvas unbedeckt. Genau das passierte auf dem Rückweg: eingefroren
 *    auf die Endbreite (601) und nach links geschoben, lagen rechts 593px
 *    blank (gemessen 2026-09-07). Deshalb die GRÖSSERE der beiden Breiten.
 * 2. **Der Inhalt muss dort stehen, wo er gerade steht, und dort landen, wo er
 *    hingehört.** Bei rechter Verankerung liegt die linke Kante der Fläche bei
 *    `rechts − Breite`; der Inhalt einer Fläche der Breite `w` stünde also um
 *    `W − w` weiter rechts. Genau das ist der Versatz — er läuft von
 *    `W − Startbreite` auf `W − Endbreite` und ist nie negativ.
 *
 * **`glide` entscheidet, ob sich überhaupt etwas bewegt.** Zwischen zwei
 * OFFENEN Zuständen ja: Der Inhalt gleitet mit, wie die Aufgabenliste (siehe
 * unten). Beim Auf- und Zuziehen nicht — dort steht er still und wird von der
 * laufenden Kante freigelegt oder zugedeckt (CONTEXT.md, Projekt-Canvas). Der
 * Versatz ist dann beidseitig 0, und die größere Breite ist ohnehin die
 * richtige: beim Öffnen die Endbreite, beim Zuziehen die Startbreite.
 *
 * Warum beim Schrumpfen überhaupt Bewegung sein MUSS: Ein Zettel, der
 * maximiert bei x=417 sitzt, gehört geteilt bei x=1009 hin — die linke Kante
 * rückt ja nach rechts. Diesen Weg legt er entweder in einem Bild zurück
 * (sichtbarer Sprung) oder in denselben 250ms wie die Kante. Entscheidung mit
 * Sven, 2026-09-07: mitgleiten.
 *
 * Getragen wird das von `translate`, nicht von `left`: Eine Verschiebung
 * kostet kein Layout — der ganze Grund, aus dem die Breite eingefroren wird.
 */
export function planCanvasContentMotion(
  fromWidth: number,
  toWidth: number,
  glide: boolean,
): CanvasMotionPlan {
  const width = Math.max(fromWidth, toWidth)
  return {
    width,
    shiftFrom: glide ? width - fromWidth : 0,
    shiftTo: glide ? width - toWidth : 0,
  }
}
